/**
 * 🎥 **우리 반 화상 링크** — 선생님이 한 번 붙여넣으면 열두 주 내내 쓰인다.
 *
 * ## 왜 필요한가 (2026-09-02)
 * 지금까지 수업 화상은 반 코드에서 **자동으로** 방 이름을 만들어 `meet.jit.si` 로 갔다.
 * 선생님이 아무것도 안 해도 되는 건 좋았지만 둘이 걸린다:
 *
 * ① **이름만 맞히면 누구나 들어온다.** 긴 이름을 만들고 「잠금을 걸어 주세요」라고
 *    안내하지만, 선생님이 잊으면 그대로 뚫린다. 아이들이 얼굴을 켜고 있는 방이다.
 * ② 학교·학부모에게 익숙한 도구(웨일온 등)를 쓸 길이 없었다. 웨일온은 **비밀번호**를
 *    걸 수 있어 ①의 답이 되고, 참여자는 계정 없이 링크로 들어온다(불변식 1 과 맞다).
 *
 * 그래서 **주소를 받아 둔다.** 어느 서비스냐를 코드에 안 박으므로 줌·구글밋도 된다.
 *
 * ## 🔴 안 넣으면 열리지 않는다 (2026-09-03에 바꾸었다)
 * 전엔 비워 두면 반 코드로 만든 `meet.jit.si` 방으로 갔다 — 「아무것도 안 해도
 * 수업은 굴러간다」고 적어 두었는데, **사실이 아니었다.** 그 방은 8x8 이 데모로만
 * 허용해서 **통화가 5분 뒤 끊긴다.** 선생님은 될 줄 알고 수업을 시작하고,
 * 아이들이 다 들어온 뒤에 끊긴다. 게다가 이름만 맞히면 누구나 들어오는 방이다.
 *
 * 화상은 **운영자가 고른다**(웨일온·구글밋·줌·Jitsi — `MEET_KINDS`).
 * 🔴 한때 여기 「웨일온으로 가기로 정했다」라고 적혀 있었다. 하루 만에 거짓이 됐다 —
 * 하나로 정해 두면 그게 안 되는 사람에게는 길이 없어서 고르게 바꿨다(2026-09-03).
 * 어느 서비스냐는 **코드에 안 박는다** — 그게 이 설계의 요점이다.
 *
 * 되는 척하다 아이들 앞에서 끊기는 것보다,
 * **아직 준비 안 됐다고 미리 말하는 편이 낫다.** 그래서 폴백을 없앤다.
 */

/** 붙여넣을 수 있는 주소의 최대 길이. 회의 링크는 길어야 200자 남짓이다. */
export const CLASS_MEET_URL_MAX = 500;

/**
 * 🔴 **바깥에서 온 주소를 믿지 않는다**(불변식 7).
 *
 * 이 값은 선생님이 붙여넣고 **아이 화면이 눌러서 새 창을 연다.** `javascript:` 같은
 * 활성 스킴이 들어오면 그 자리가 그대로 실행 통로가 된다. 그래서 `https` 만 받는다
 * (`http` 도 안 받는다 — 카메라·마이크를 쓰는 페이지는 어차피 안전한 연결이어야 한다).
 *
 * 못 쓰는 값이면 `null`. **「비었다」와 「이상하다」를 부르는 쪽이 가른다** —
 * 화면이 「지웠어요」와 「주소가 이상해요」를 다르게 말해야 하기 때문이다.
 */
const INVISIBLE = new RegExp(
  "[\\u0000-\\u001f\\u007f\\u00a0\\u200b-\\u200f\\u202a-\\u202e" +
    "\\u2028\\u2029\\u2060-\\u2064\\u2066-\\u2069\\u3000\\ufeff\\ufff9-\\ufffb]",
);

export function safeMeetUrl(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s || s.length > CLASS_MEET_URL_MAX) return null;
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return null;
  }
  if (u.protocol !== "https:") return null;
  /**
   * 눈에 **안 보이는 글자**를 막는다 — 제어문자·폭 없는 공백·좌우쓰기 표식·BOM.
   * 이런 글자는 주소를 다른 것처럼 보이게 꾸미는 데 쓰인다. 선생님이 붙여넣은 것을
   * 아이가 눌러서 새 창을 여는 자리라 더 조심한다.
   *
   * 🔴 **코드포인트로 적는다.** 글자를 그대로 적으면 파일에 안 보이는 값이 들어가
   * 아무도 못 읽는다(실제로 한 번 그렇게 박혔다 — `pnpm check:ctrl` 이 잡는다).
   */
  if (INVISIBLE.test(s)) return null;
  return u.toString();
}

/** 붙여넣은 값이 왜 안 되는지 — 화면이 그대로 보여 줄 한 줄. */
export function meetUrlSay(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null; // 비우는 것은 「지우기」다 — 잘못이 아니다
  if (safeMeetUrl(s)) return null;
  if (s.length > CLASS_MEET_URL_MAX) return "주소가 너무 길어요. 회의 링크만 붙여넣어 주세요.";
  if (/^http:\/\//i.test(s)) return "`https` 로 시작하는 주소여야 해요(카메라·마이크를 쓰니까요).";
  return "회의 링크를 그대로 붙여넣어 주세요. `https://` 로 시작해야 해요.";
}

/**
 * 이 반이 실제로 열 주소. 선생님이 넣어 둔 것이 있으면 그것, 없으면 예전 방식.
 *
 * 🔴 저장된 값도 **여기서 한 번 더 거른다.** 표에 이상한 값이 들어가 있을 수 있고
 * (옛 기록·손으로 고친 것), 그걸 그대로 아이 화면에 넘기면 안 된다.
 */
/**
 * 이 반의 화상 주소. 선생님이 넣은 것만 쓴다 — **없으면 `null`**(폴백 없음).
 *
 * 🔴 `code` 는 이제 안 쓴다. 받는 자리를 남겨 두면 「언젠가 다시 방을 만들겠지」로
 * 읽힐다 — 인자를 아예 없앨다.
 */
export function classMeetUrl(saved: unknown): string | null {
  return safeMeetUrl(saved);
}

/** 선생님이 제 링크를 넣어 둔 반인가 — 화면이 「지금 무엇을 쓰는지」 말할 수 있게. */
export function hasOwnMeetUrl(saved: unknown): boolean {
  return safeMeetUrl(saved) !== null;
}
